// src/components/crm/QuoteAttentionStrip.js
// SEAM 8 — attention & staleness. Marker: qattn-8c4e1f92-strip
//
// The fourth internal strip on the estimate page, beside the pipeline stage
// picker, the follow-up strip and the activity timeline. Operator-only, kept
// OUTSIDE the document card (which mirrors the customer PDF).
//
// READ-ONLY AND PROP-FED. It opens no listener and calls nothing: the quote and
// the stage list are already in scope on the page, and every field the rule
// reads is already on the quote. So this strip costs one function call.
//
// IT SAYS WHY, NOT JUST THAT. The one-word chip lives in the list; here there
// is room for the sentences, so every reason is shown rather than only the most
// urgent. That matters because the commonest pair by far is "gone quiet" plus
// "no next step" — and the second half is the one you can act on.
import React from 'react';
import { AlertTriangle, Check } from 'lucide-react';
import { findPipelineStage, resolveQuoteAttention } from '../../utils/quoteSchema';

const QuoteAttentionStrip = ({ quote, pipelineStages, companySettings, docWord = 'Estimate' }) => {
    if (!quote) return null;

    // Stage list still loading → the rule silences the two stage-keyed rungs
    // rather than guessing. Nothing is rendered until it can be right.
    const stage = pipelineStages ? findPipelineStage(quote.pipelineStageId, pipelineStages) : null;
    const attention = resolveQuoteAttention(quote, stage, {
        timeZone: companySettings?.timezone,
    });

    // Snoozed: the follow-up strip directly below already says "Automatic
    // reminders paused until <date>" in plain words. Repeating it here would be
    // the same fact twice on one screen.
    if (attention.snoozed) return null;

    const word = String(docWord || 'Estimate').toLowerCase();

    if (attention.level !== 'attention') {
        // Deliberately quiet, not silent. An operator who has just cleared an
        // estimate should see that it is clear — an empty space reads as "the
        // feature is broken", not as "nothing to do".
        return (
            <div className="max-w-4xl mx-auto mb-4 px-4 sm:px-0">
                <div className="flex items-center gap-2 text-xs text-gray-500 bg-gray-50 border border-gray-200 rounded-lg px-3 py-2">
                    <Check size={14} className="text-green-600 shrink-0" />
                    <span>Nothing needs chasing on this {word}.</span>
                </div>
            </div>
        );
    }

    return (
        <div className="max-w-4xl mx-auto mb-4 px-4 sm:px-0">
            <div className="bg-amber-50 border border-amber-200 rounded-lg px-4 py-3">
                <div className="flex items-start gap-2.5">
                    <AlertTriangle size={16} className="text-amber-600 mt-0.5 shrink-0" />
                    <div className="min-w-0">
                        <p className="text-sm font-semibold text-amber-800">
                            This {word} needs you
                        </p>
                        <ul className="mt-1 space-y-0.5">
                            {attention.reasons.map((reason, i) => (
                                <li key={i} className="text-sm text-amber-700">{reason}</li>
                            ))}
                        </ul>
                        {/* The honest limit of the feature, said out loud: this is
                            a display, not a reminder. Nothing here emails anyone. */}
                        <p className="mt-1.5 text-xs text-amber-600">
                            Internal only — nothing has been sent to the customer about this.
                        </p>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default QuoteAttentionStrip;
